// Copyright (c) Ping. All rights reserved.
//
// Dexie-backed local search adapter for the global search modal.
//
// Conversations, messages, and attachments live in IndexedDB; none of
// these have a full-text index (sqlite-style FTS isn't available on
// IDB), so we do a linear case-insensitive substring scan. Scoped
// caps keep the work bounded:
//   - conversations: scan all rows; the table tops out at hundreds
//     even for heavy users.
//   - messages: cap the scan at MESSAGE_SCAN_CAP rows ordered by
//     `sentAt desc` so recent messages always win.
//   - attachments: scan all rows whose `state === 'uploaded'`.
//
// The signal arg is honoured cooperatively — Dexie itself doesn't
// support AbortSignal, so we check `signal.aborted` between phases.

import {
	type GlobalSearchConversation,
	type GlobalSearchFile,
	type GlobalSearchLocalResults,
	type GlobalSearchMessage,
	lastMessagePreview,
} from '@ping/ui';

import { db } from '@core/persistence/db';

const MESSAGE_SCAN_CAP = 2000;
const PER_SECTION_CAP = 20;

export async function localSearch(
	query: string,
	signal: AbortSignal,
): Promise<GlobalSearchLocalResults> {
	const needle = query.trim().toLowerCase();
	if (needle.length === 0) {
		return { conversations: [], messages: [], files: [] };
	}

	const [conversations, messages, files] = await Promise.all([
		searchConversations(needle, signal),
		searchMessages(needle, signal),
		searchFiles(needle, signal),
	]);

	if (signal.aborted) {
		return { conversations: [], messages: [], files: [] };
	}

	return { conversations, messages, files };
}

async function searchConversations(
	needle: string,
	signal: AbortSignal,
): Promise<readonly GlobalSearchConversation[]> {
	// `db.conversations` is small — full scan is fine.
	const rows = await db.conversations.toArray();
	if (signal.aborted) return [];

	// Keep `lastMessageAt` alongside the wire result so we can recency-sort
	// before stripping it (the wire type only carries the preview string).
	const hits: Array<GlobalSearchConversation & { readonly _sortAt: number }> = [];
	for (const row of rows) {
		if (hits.length >= PER_SECTION_CAP) break;
		const name = (row.name ?? '').toLowerCase();
		const peer = (row.peerAccountId ?? '').toLowerCase();
		if (name.includes(needle) || peer.includes(needle)) {
			hits.push({
				id: row.id,
				name: row.name ?? '',
				kind: row.kind,
				// Surface the real last-message preview — the same
				// glyph-encoded string the conversation list renders — so the
				// modal's shared <MessagePreview> shows the proper kind icon
				// ("📷 Photo" → 📷 glyph) instead of a bare timestamp.
				...(row.lastMessagePreview !== undefined && row.lastMessagePreview.length > 0
					? { lastMessagePreview: row.lastMessagePreview }
					: {}),
				_sortAt: row.lastMessageAt ?? 0,
			});
		}
	}

	// Recency-first: most-recently-active conversations come first.
	hits.sort((a, b) => b._sortAt - a._sortAt);

	return hits.map(({ _sortAt, ...conv }) => conv);
}

async function searchMessages(
	needle: string,
	signal: AbortSignal,
): Promise<readonly GlobalSearchMessage[]> {
	// `sentAt` is NOT in the Dexie index list for `messages` (see
	// apps/web/src/core/persistence/db.ts:156) — `orderBy('sentAt')`
	// throws SchemaError. Iterate in primary-key order with a hard cap,
	// filter in memory, then recency-sort the matches we keep. Adding
	// a `sentAt` index would be cleaner but requires a schema version
	// bump + migration of every existing user's IDB.
	type MsgRow = {
		readonly id: string;
		readonly conversationId: string;
		readonly body?: string;
		readonly deletedAt?: number;
		readonly sentAt: number;
	};
	const matches: Array<MsgRow & { readonly previewText: string }> = [];
	let scanned = 0;
	await db.messages.each((row) => {
		if (scanned >= MESSAGE_SCAN_CAP) return;
		scanned++;
		if (row.deletedAt !== undefined) return;
		// `body` holds the raw `{kind,payload}` envelope for every non-text
		// kind (poll, voice, image, …). Decode it to the human-readable
		// preview ("Poll: nice?", "🎤 Voice message · 0:12") so we match on —
		// and render — real text, never the JSON wrapper. Sidecars (read
		// receipts, reactions, profile intros) decode to empty and drop out.
		const previewText = lastMessagePreview(row.body ?? '') ?? '';
		if (previewText.length === 0) return;
		if (!previewText.toLowerCase().includes(needle)) return;
		matches.push({ ...row, previewText });
	});
	if (signal.aborted) return [];

	// Recency-first ordering applied post-filter so we don't burn the
	// scan budget on stale rows that don't match anyway.
	matches.sort((a, b) => b.sentAt - a.sentAt);

	const top = matches.slice(0, PER_SECTION_CAP);
	const convNames = await collectConversationNames(
		new Set(top.map((m) => m.conversationId)),
	);
	if (signal.aborted) return [];

	return top.map((row) => ({
		id: row.id,
		conversationId: row.conversationId,
		conversationName: convNames.get(row.conversationId) ?? 'Conversation',
		snippet: makeSnippet(row.previewText, needle),
		sentAt: row.sentAt,
	}));
}

async function searchFiles(
	needle: string,
	signal: AbortSignal,
): Promise<readonly GlobalSearchFile[]> {
	// Attachments table is small in practice; full scan over `uploaded`
	// rows. `state` is indexed so we can pre-filter cheaply.
	const rows = await db.attachments_meta.where('state').equals('uploaded').toArray();
	if (signal.aborted) return [];

	// Resolve each attachment's conversation via its parent message.
	const messageIds = new Set(rows.map((r) => r.messageId));
	const messageRows = await db.messages.where('id').anyOf([...messageIds]).toArray();
	if (signal.aborted) return [];
	const msgToConv = new Map<string, string>();
	for (const m of messageRows) msgToConv.set(m.id, m.conversationId);

	const hits: GlobalSearchFile[] = [];
	for (const row of rows) {
		if (hits.length >= PER_SECTION_CAP) break;
		const filename = (row.filename ?? '').toLowerCase();
		if (!filename.includes(needle)) continue;
		const convId = msgToConv.get(row.messageId);
		if (convId === undefined) continue;
		hits.push({
			id: row.id,
			messageId: row.messageId,
			conversationId: convId,
			filename: row.filename ?? '(unnamed)',
			...(row.mimeType ? { mimeType: row.mimeType } : {}),
			...(row.sizeBytes !== undefined ? { sizeBytes: row.sizeBytes } : {}),
		});
	}

	return hits;
}

async function collectConversationNames(ids: Set<string>): Promise<Map<string, string>> {
	if (ids.size === 0) return new Map();
	const rows = await db.conversations.where('id').anyOf([...ids]).toArray();
	const out = new Map<string, string>();
	for (const row of rows) {
		out.set(row.id, row.name ?? (row.kind === 'group' ? 'Group' : 'Direct chat'));
	}
	return out;
}

function makeSnippet(body: string, needle: string): string {
	// Window the matched term so long messages don't render their
	// whole text as the snippet. Pull ~40 chars on either side of the
	// first match.
	const idx = body.toLowerCase().indexOf(needle);
	if (idx < 0) return body.slice(0, 120);
	const start = Math.max(0, idx - 40);
	const end = Math.min(body.length, idx + needle.length + 40);
	const prefix = start === 0 ? '' : '…';
	const suffix = end === body.length ? '' : '…';
	return `${prefix}${body.slice(start, end)}${suffix}`;
}
